import React from 'react';
import { API } from '../api';

/* ====================================================================
   CLUB EMAILS — a member choosing what their club sends them, on their
   own profile. The same choices as the link at the bottom of every club
   email, without having to find one.

   Each tick saves as it is made. Unticking the last one unsubscribes, so
   that one asks first.
   ==================================================================== */

function ClubEmailsCard({ showToast, showConfirm, profileEmail }) {
  // The app's own dialog where the caller has one; the browser's otherwise.
  const ask = showConfirm || (message => Promise.resolve(window.confirm(message)));
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/mailing-list/me`);
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) { setError(d.error || ''); setData(null); return; }
        setData(d);
        setError('');
      } catch {
        if (!cancelled) setError('');
      }
    })();
    return () => { cancelled = true; };
    // Refetched when the profile's address changes: the list follows it.
  }, [profileEmail]);

  const save = async (interests) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/mailing-list/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interests }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'That did not save.');
      setData(d);
      showToast && showToast(d.subscribed ? 'Saved' : "You're unsubscribed from club emails");
    } catch (e) {
      showToast && showToast(e.message, 'red');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (id, on) => {
    const current = data.topics.filter(t => t.on).map(t => t.id);
    const next = on ? [...current, id] : current.filter(x => x !== id);
    if (!next.length) {
      const ok = await ask("That's the last one ticked.\n\nUnticking it unsubscribes you from all club news emails. You'll still get emails about competitions you enter and your membership.");
      if (!ok) return;
    }
    save(next);
  };

  // Nothing to manage without an address; the profile already says to add one.
  if (!data && !error) return null;

  return (
    <div className="w3-card w3-round w3-white w3-margin-top">
      <div className="w3-container w3-theme-l4 w3-round-top" style={{ padding: '12px 16px' }}>
        <strong><i className="fa fa-envelope w3-margin-right"></i>Club emails</strong>
      </div>
      <div className="w3-container w3-padding">
        {!data ? (
          <p className="w3-small w3-text-grey" style={{ margin: 0 }}>{error}</p>
        ) : data.erased ? (
          <p className="w3-small" style={{ margin: 0 }}>
            You asked {data.clubName} to delete your mailing-list details, so you won't get club news emails.
            Contact the club if you'd like to be added again.
          </p>
        ) : (
          <>
            <p className="w3-small w3-text-grey" style={{ marginTop: 0 }}>
              What {data.clubName} emails you about, at <strong>{data.email}</strong>.
              {data.subscribed ? '' : " You're unsubscribed at the moment. Tick anything you'd like to get."}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 24px' }}>
              {data.topics.map(t => (
                <label key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: busy ? 'default' : 'pointer', minHeight: 40 }}>
                  <input type="checkbox" className="w3-check" style={{ top: 0, width: 20, height: 20 }}
                    checked={t.on} disabled={busy}
                    onChange={e => toggle(t.id, e.target.checked)} />
                  <span>{t.label}</span>
                </label>
              ))}
            </div>
            {data.subscribed && (
              <button type="button" className="w3-button w3-small w3-round w3-border w3-margin-top" disabled={busy}
                onClick={async () => {
                  const ok = await ask('Unsubscribe from all club news emails?\n\nYou\'ll still get emails about competitions you enter and your membership.');
                  if (ok) save([]);
                }}>
                Unsubscribe from all
              </button>
            )}
            <p className="w3-small w3-text-grey" style={{ marginBottom: 0 }}>
              Emails about competitions you've entered and your membership always come through.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default ClubEmailsCard;
